import * as React from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { useTitleStore } from 'src/stores/useTitleStore';
import { Button } from 'src/registry/ui/Button';
import { Badge } from 'src/registry/ui/Badge';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { commonMessages, generalMessages } from 'src/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { useGetReferenceRate } from 'src/features/referenceRates/api/useGetReferenceRate';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import UpdateReferenceRateFormModal from 'src/features/referenceRates/components/forms/UpdateReferenceRateFormModal';
import DeleteReferenceRateFormModal from 'src/features/referenceRates/components/forms/DeleteReferenceRateFormModal';
import ReferenceRateValuesTable from 'src/features/referenceRateValues/components/tables/ReferenceRateValuesTable';

const ReferenceRateDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { referenceRateId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(referenceRatesMessages.referenceRateDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: referenceRate } = useGetReferenceRate({ id: referenceRateId });

    const isActiveAsString = referenceRate?.is_active ? 'true' : 'false';
    const currentValue = referenceRate?.current_value;

    const dashboardBlockData = {
        section1: [
            {
                title: referenceRatesMessages.code,
                value: referenceRate?.code ?? '-',
            },
            {
                title: commonMessages.currency,
                value: referenceRate?.currency ? <CellCurrencyBadge currency={referenceRate.currency} /> : '-',
            },
            {
                // Null means no value in the series covers today, which is the state that makes an
                // indexed pricing policy fail to save - so it is spelled out rather than dashed.
                title: referenceRatesMessages.currentValue,
                value:
                    currentValue && referenceRate ? (
                        formatNumber(currentValue.amount / 100, {
                            style: 'currency',
                            currency: referenceRate.currency,
                        })
                    ) : (
                        <span className="text-gray-500">{formatMessage(referenceRatesMessages.noCurrentValue)}</span>
                    ),
            },
            {
                title: referenceRatesMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: generalMessages.description,
                value: referenceRate?.description || '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to="/reference-rates">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(referenceRatesMessages.backToAllReferenceRates)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {referenceRate?.name}
                        </SectionBlockTitle>

                        {permissionManageServicePlans && referenceRate && (
                            <div className="flex flex-row gap-2">
                                <UpdateReferenceRateFormModal
                                    selectedReferenceRate={referenceRate}
                                    semantics={{
                                        entryName: formatMessage(referenceRatesMessages.rate).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateReferenceRateFormModal>
                                <DeleteReferenceRateFormModal
                                    selectedReferenceRate={referenceRate}
                                    onDeleted={() => navigate('/reference-rates')}
                                    semantics={{
                                        entryName: formatMessage(referenceRatesMessages.rate).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteReferenceRateFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={0} />
                </SectionBlock>

                <div className="grid flex-3 grid-cols-1 pr-8">
                    <ReferenceRateValuesTable currency={referenceRate?.currency} />
                </div>
            </div>
        </>
    );
};

export default ReferenceRateDashboard;
